import { inject, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { patchState, signalStoreFeature, type, withMethods, withProps, withState } from '@ngrx/signals'
import { eventGroup, Events, injectDispatch } from '@ngrx/signals/events'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import {
  catchError,
  defer,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  from,
  fromEvent,
  ignoreElements,
  merge,
  NEVER,
  of,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs'

import { pluralize } from '@app/core/pluralize'
import { Timespan } from '@app/modules/time/core/timespan.enum'
import { ToastService } from '@app/overlays/toast/toast.service'
import { TrackService } from '@app/services/track.service'

import type { RecordingType } from './recorder.service'
import { RecorderService } from './recorder.service'

const recordingFeatureEvents = eventGroup({
  source: 'recordingFeature',
  events: {
    stopRecording: type<void>(),
  },
})

interface State {
  recordingEndsAt: string | undefined
  maxDurationMs: number
  minDurationMs: number
  error: boolean
}

const DEFAULT_MIN_DURATION_MS = 2 * Timespan.Second
const DEFAULT_MAX_DURATION_MS = 30 * Timespan.Second
const RECORDING_OFFSET_MS = 500

export const recordingFeature = (
  recordingType: RecordingType,
  minDurationMs = DEFAULT_MIN_DURATION_MS,
  maxDurationMs = DEFAULT_MAX_DURATION_MS,
) =>
  signalStoreFeature(
    withState<State>({
      recordingEndsAt: undefined,
      maxDurationMs,
      minDurationMs,
      error: false,
    }),
    withProps((store, recorderService = inject(RecorderService)) => {
      const visibilityChange$ = fromEvent(document, 'visibilitychange')

      const _media = signal<{ blob: Blob; url: string } | undefined>(undefined)

      const stream$ = defer(() =>
        from(recorderService.requestMediaStream(recordingType)).pipe(
          switchMap(stream =>
            NEVER.pipe(
              startWith(stream),
              finalize(() => {
                stream.getTracks().forEach(track => track.stop())
              }),
            ),
          ),
        ),
      ).pipe(
        catchError(() => {
          patchState(store, { error: true })
          return EMPTY
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      )

      const _recorder$ = defer(() =>
        stream$.pipe(
          switchMap(stream =>
            from(recorderService.requestMediaRecorder(recordingType, stream)).pipe(
              switchMap(recorder =>
                NEVER.pipe(
                  startWith(recorder),
                  finalize(() => {
                    recorder.stop()
                  }),
                ),
              ),
            ),
          ),
        ),
      ).pipe(
        catchError(() => {
          patchState(store, { error: true })
          return EMPTY
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      )

      const permissionState$ = recorderService
        .permissionState(recordingType)
        .pipe(shareReplay({ bufferSize: 1, refCount: true }))

      return {
        _media,
        media: _media.asReadonly(),
        _visibilityHide$: visibilityChange$.pipe(filter(() => document.visibilityState === 'hidden')),
        stream$: visibilityChange$.pipe(
          startWith(0),
          switchMap(() => (document.visibilityState === 'visible' ? stream$ : of(undefined))),
        ),
        _recorder$,
        _permissionRevoke$: permissionState$.pipe(
          skip(1),
          filter(state => state !== 'granted'),
        ),
        permissionState: toSignal(permissionState$),
        _windowBlur$: fromEvent(window, 'blur'),
      }
    }),
    withMethods(
      (
        store,
        events = inject(Events),
        dispatcher = injectDispatch(recordingFeatureEvents),
        trackService = inject(TrackService),
        toastService = inject(ToastService),
      ) => ({
        clearMedia: () => store._media.set(undefined),
        clearError: () => patchState(store, { error: undefined }),
        updateMaxDurationTime: (maxDurationSeconds: number) =>
          patchState(store, { maxDurationMs: maxDurationSeconds * Timespan.Second }),
        askForPermission: rxMethod<void>(exhaustMap(() => store.stream$.pipe(take(1)))),
        stopRecording: () => dispatcher.stopRecording(),
        startRecording: rxMethod<void>(
          exhaustMap(() =>
            store._recorder$.pipe(
              tap(() => {
                trackService.track('upload', { action: 'attempt', method: 'media_recorder', type: recordingType })
              }),
              switchMap(recorder => {
                recorder.start()
                const recordingEndsAt = new Date(Date.now() + store.maxDurationMs())
                patchState(store, { recordingEndsAt: recordingEndsAt.toISOString() })
                const chunks: Blob[] = []

                return merge(
                  merge(
                    timer(new Date(recordingEndsAt.getTime() + RECORDING_OFFSET_MS)),
                    events.on(recordingFeatureEvents.stopRecording),
                    store._permissionRevoke$,
                    store._visibilityHide$,
                    store._windowBlur$,
                  ).pipe(
                    take(1),
                    finalize(() => {
                      recorder.stop()
                    }),
                    ignoreElements(),
                  ),
                  fromEvent<BlobEvent>(recorder, 'dataavailable').pipe(
                    tap(event => chunks.push(event.data)),
                    ignoreElements(),
                  ),
                  fromEvent(recorder, 'stop').pipe(
                    tap(() => {
                      const durationMs = store.maxDurationMs() - (recordingEndsAt.getTime() - Date.now())
                      if (durationMs < minDurationMs) {
                        const duration = pluralize(minDurationMs / Timespan.Second, 'second')
                        toastService.show(`This recording is too short, make it at least ${duration}.`)
                      } else {
                        const blob = new Blob(chunks, { type: RecorderService.getMediaMimeType(recordingType) })
                        store._media.set({ blob, url: URL.createObjectURL(blob) })
                      }
                      patchState(store, { recordingEndsAt: undefined })
                    }),
                  ),
                )
              }),
              take(1),
            ),
          ),
        ),
      }),
    ),
  )
